import React, { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, CornerDownRight, Plus, Trash2 } from "lucide-react";
import { Button, Switch } from "./ui/kit";
import { FazeDot, nastavFaze } from "../lib/faze";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";

// Nastavení → Fáze a úkoly: firma si přejmenuje fáze, do Poptávky a mezi Nabídku
// a Objednáno přidá vlastní mezikroky (např. Zpracování poptávky, Finalizace)
// a vypne plánování zaměření v kalendáři (posouvá se tlačítkem). Kódy fází a brány
// zůstávají (backend faze.py), mění jen majitel; ostatní vidí, jak se co jmenuje.

const PORADI = ["NOVY", "ZAMERENI", "NABIDKA", "OBJEDNANO", "REALIZACE", "DOKONCENO", "ZTRACENO"];
const MAX_MEZIKROKU = 5;                          // na jednu fázi
const FAZE_MEZIKROKU = ["NOVY", "NABIDKA"];
let dalsi = 0;

export default function FazeNastaveni({ muzeUpravit, onUlozeno }) {
  const { setCompany } = useAuth();
  const [data, setData] = useState(null);          // {nazvy, mezikroky, vychozi} ze serveru
  const [nazvy, setNazvy] = useState({});
  const [kroky, setKroky] = useState([]);           // [{id?, klic, nazev, faze}]
  const [kalendar, setKalendar] = useState(true);   // zaměření se plánuje v kalendáři
  const [predNabidkou, setPredNabidkou] = useState(true);   // bez umístění a výkonu nejde nabídka
  const [stav, setStav] = useState("");             // "" | "uklada" | "ulozeno"
  const [chyba, setChyba] = useState("");

  const prijmout = (d) => {
    setData(d);
    setNazvy(Object.fromEntries(PORADI.map((k) => [k, d.nazvy[k] === d.vychozi[k] ? "" : d.nazvy[k]])));
    setKroky(d.mezikroky.map((m) => ({ ...m, klic: m.id })));
    setKalendar(d.zamereni_v_kalendari !== false);
    setPredNabidkou(d.zamereni_pred_nabidkou !== false);
  };
  useEffect(() => {
    api.get("/api/faze-firmy").then((r) => prijmout(r.data)).catch((e) => setChyba(apiError(e.response?.data?.detail)));
  }, []);

  const telo = () => ({
    nazvy: Object.fromEntries(Object.entries(nazvy).filter(([, v]) => v.trim())),
    mezikroky: kroky.map((m) => ({ id: m.id || null, nazev: m.nazev, faze: m.faze })),
    zamereni_v_kalendari: kalendar,
    zamereni_pred_nabidkou: predNabidkou,
  });
  const zmeneno = useMemo(() => {
    if (!data) return false;
    const puvodni = {
      nazvy: Object.fromEntries(PORADI.filter((k) => data.nazvy[k] !== data.vychozi[k]).map((k) => [k, data.nazvy[k]])),
      mezikroky: data.mezikroky.map((m) => ({ id: m.id, nazev: m.nazev, faze: m.faze })),
      zamereni_v_kalendari: data.zamereni_v_kalendari !== false,
      zamereni_pred_nabidkou: data.zamereni_pred_nabidkou !== false,
    };
    return JSON.stringify(puvodni) !== JSON.stringify(telo());
  }, [data, nazvy, kroky, kalendar, predNabidkou]); // eslint-disable-line react-hooks/exhaustive-deps

  const ulozit = async () => {
    setStav("uklada");
    setChyba("");
    try {
      const { data: d } = await api.put("/api/faze-firmy", telo());
      prijmout(d);
      const ulozene = { nazvy: Object.fromEntries(PORADI.filter((k) => d.nazvy[k] !== d.vychozi[k]).map((k) => [k, d.nazvy[k]])),
        mezikroky: d.mezikroky, zamereni_v_kalendari: d.zamereni_v_kalendari,
        zamereni_pred_nabidkou: d.zamereni_pred_nabidkou };
      nastavFaze(ulozene);
      setCompany((c) => ({ ...c, faze_nastaveni: ulozene }));   // celá aplikace hned s novými názvy
      setStav("ulozeno");
      setTimeout(() => setStav(""), 1800);
      onUlozeno && onUlozeno();
    } catch (e) {
      setStav("");
      setChyba(apiError(e.response?.data?.detail) || e.message);
    }
  };

  // Posun o jedno místo mezi mezikroky téže fáze.
  const posun = (klic, o) => setKroky((ks) => {
    const i = ks.findIndex((m) => m.klic === klic);
    const vFazi = ks.map((m, k) => (m.faze === ks[i].faze ? k : -1)).filter((k) => k >= 0);
    const j = vFazi[vFazi.indexOf(i) + o];
    if (j === undefined) return ks;
    const n = [...ks];
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });

  const mezikrokyFaze = (faze) => {
    const ve = kroky.filter((m) => m.faze === faze);
    return (
      <li className="pl-7 space-y-2" data-testid={`mezikroky-${faze}`}>
        {ve.map((m, j) => (
          <div key={m.klic} className="flex items-center gap-1.5">
            <CornerDownRight size={16} className="text-muted-2 shrink-0" />
            <input className="field !h-11 md:!h-10 flex-1 min-w-0" value={m.nazev} disabled={!muzeUpravit} maxLength={30}
              placeholder={faze === "NOVY" ? "Např. Zpracování poptávky" : "Název mezikroku"} aria-label="Název mezikroku"
              data-testid={`mezikrok-nazev-${faze}-${j}`}
              onChange={(e) => setKroky((ks) => ks.map((x) => (x.klic === m.klic ? { ...x, nazev: e.target.value } : x)))} />
            {muzeUpravit && (
              <>
                {/* přeřazení jen na počítači — na telefonu by zúžilo pole s názvem */}
                <button type="button" onClick={() => posun(m.klic, -1)} disabled={j === 0} aria-label="Výš"
                  className="hidden md:inline-flex h-10 w-9 items-center justify-center text-muted hover:text-ink disabled:opacity-30"><ArrowUp size={15} /></button>
                <button type="button" onClick={() => posun(m.klic, 1)} disabled={j === ve.length - 1} aria-label="Níž"
                  className="hidden md:inline-flex h-10 w-9 items-center justify-center text-muted hover:text-ink disabled:opacity-30"><ArrowDown size={15} /></button>
                <button type="button" onClick={() => setKroky((ks) => ks.filter((x) => x.klic !== m.klic))} aria-label="Smazat mezikrok"
                  data-testid={`mezikrok-smazat-${faze}-${j}`}
                  className="tap md:min-h-0 h-10 w-9 inline-flex items-center justify-center text-muted hover:text-danger"><Trash2 size={15} /></button>
              </>
            )}
          </div>
        ))}
        {muzeUpravit && ve.length < MAX_MEZIKROKU && (
          <button type="button" onClick={() => setKroky((ks) => [...ks, { klic: `novy-${++dalsi}`, nazev: "", faze }])}
            data-testid={`mezikrok-pridat-${faze}`}
            className="tap md:min-h-0 h-9 inline-flex items-center gap-1.5 t-meta font-700 text-accent hover:text-accent-hover">
            <Plus size={15} /> Přidat mezikrok
          </button>
        )}
      </li>
    );
  };

  if (!data) return chyba ? <div className="card p-5 t-meta text-danger">{chyba}</div> : null;

  const pole = (klic) => (
    <input className="field !h-11 md:!h-10 w-full min-w-0" value={nazvy[klic] ?? ""} disabled={!muzeUpravit} maxLength={30}
      placeholder={data.vychozi[klic]} onChange={(e) => setNazvy((n) => ({ ...n, [klic]: e.target.value }))}
      aria-label={`Název fáze ${data.vychozi[klic]}`} data-testid={`faze-nazev-${klic}`} />
  );

  return (
    <div className="card p-5 md:p-6" data-testid="faze-nastaveni">
      <div className="mb-4">
        <h2 className="t-sekce font-700 text-ink">Fáze obchodu</h2>
        <p className="t-meta text-muted mt-0.5">
          Pojmenuj fáze po svém. Do poptávky a mezi nabídku a objednávku můžeš přidat vlastní mezikroky — obchodník
          mezi nimi posouvá ručně, do objednávky dál vede schválení nabídky. Prázdné pole = výchozí název.
        </p>
      </div>

      <ol className="space-y-2 md:max-w-[560px]">
        {PORADI.map((klic, i) => (
          <React.Fragment key={klic}>
            <li className="flex items-center gap-2.5">
              <span className="w-5 shrink-0 flex justify-center"><FazeDot faze={klic} /></span>
              {pole(klic)}
            </li>
            {FAZE_MEZIKROKU.includes(klic) && mezikrokyFaze(klic)}
            {klic === "ZAMERENI" && (
              <li className="pl-7 flex items-start gap-3" data-testid="zamereni-kalendar">
                <div className="min-w-0 flex-1">
                  <div className="t-telo text-ink">Plánovat v kalendáři</div>
                  <div className="t-meta text-muted">
                    {kalendar ? "Do fáze se přejde naplánováním termínu." : "Bez termínu — z poptávky se posouvá tlačítkem „Dál“."}
                  </div>
                </div>
                <Switch checked={kalendar} onChange={setKalendar} disabled={!muzeUpravit}
                  label="Zaměření plánovat v kalendáři" testid="zamereni-kalendar-prepinac" />
              </li>
            )}
            {klic === "ZAMERENI" && (
              <li className="pl-7 flex items-start gap-3" data-testid="zamereni-pred-nabidkou">
                <div className="min-w-0 flex-1">
                  <div className="t-telo text-ink">Vyžadovat před nabídkou</div>
                  <div className="t-meta text-muted">
                    {predNabidkou ? "Nabídka jde vytvořit až s vyplněným umístěním a výkonem."
                      : "Nabídka jde vytvořit kdykoli — umístění a výkon jsou nepovinné."}
                  </div>
                </div>
                <Switch checked={predNabidkou} onChange={setPredNabidkou} disabled={!muzeUpravit}
                  label="Zaměření vyžadovat před nabídkou" testid="zamereni-pred-nabidkou-prepinac" />
              </li>
            )}
            {klic === "DOKONCENO" && i < PORADI.length - 1 && <li aria-hidden="true" className="h-px bg-line-softer my-1" />}
          </React.Fragment>
        ))}
      </ol>

      {chyba && <div className="t-meta text-danger mt-3">{chyba}</div>}
      {muzeUpravit && (
        <div className="mt-4 flex justify-end">
          <Button onClick={ulozit} disabled={!zmeneno || stav === "uklada"} className="w-full md:w-auto !h-11"
            stav={stav === "uklada" ? "nacita" : stav === "ulozeno" ? "hotovo" : undefined} data-testid="faze-ulozit">
            Uložit fáze
          </Button>
        </div>
      )}
    </div>
  );
}
